import { useEffect, useId, useRef, useState } from 'react';
import { usePopoverPlacement } from '@/hooks/usePopoverPlacement';
import { cn } from '@/lib/cn';

/**
 * 筛选胶囊的外观（CR-082）：日期区间（`RangeField`）和下面的 `FilterChip` 共用，一排胶囊长得一样。
 * 高度 `touch-sm`（电脑 28 / 手机 32），和分段按钮一样高。
 */
export function chipClass(open: boolean): string {
  return cn(
    'inline-flex min-h-touch-sm max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-body shadow-xs transition-colors',
    open ? 'border-brand-500 bg-brand-50' : 'border-rule-strong bg-surface hover:bg-surface-2',
  );
}

export interface ChipOption<T extends string> {
  /** `null` 代表「全部」 */
  value: T | null;
  label: string;
}

/**
 * 筛选胶囊（CR-082）：「类别 全部」—— 灰字是筛什么，黑字是现在的值；点开是选项弹层。
 *
 * 代替原来筛选卡片里一排带标签的下拉框：一排胶囊占一行，扫一眼就知道现在筛了什么。
 * 弹层用 `usePopoverPlacement` 定位（DESIGN-SYSTEM「下拉面板不写死往下开」），Esc 或点外面关掉。
 */
export function FilterChip<T extends string>({
  label,
  value,
  options,
  onChange,
  action,
}: {
  label: string;
  value: T | null;
  options: ReadonlyArray<ChipOption<T>>;
  onChange: (next: T | null) => void;
  /** 走查用的 `data-action` */
  action?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  usePopoverPlacement(open, rootRef, listRef);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus({ preventScroll: true });
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = options.find((option) => option.value === value) ?? options[0];

  return (
    <div ref={rootRef} className="relative inline-block max-w-full">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        data-action={action}
        onClick={() => setOpen((v) => !v)}
        className={chipClass(open)}
      >
        <span className="text-ink-3">{label}</span>
        <span className="truncate font-semibold text-ink">{current?.label}</span>
      </button>

      {open && (
        <div
          ref={listRef}
          id={listId}
          role="menu"
          aria-label={label}
          className="absolute left-0 z-dropdown min-w-44 overflow-y-auto rounded-lg border border-rule-strong bg-popover p-1 shadow-pop"
        >
          <div className="px-2 pb-1 pt-1.5 text-caption font-medium text-ink-4">{label}</div>
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value ?? '__all__'}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex min-h-touch-sm w-full items-center whitespace-nowrap rounded-md px-2 text-left text-body',
                  'hover:bg-ink/6 focus:bg-ink/6 focus:outline-none',
                  selected ? 'font-semibold text-brand-700' : 'text-ink',
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
